/**
 * WorkflowKanbanPage — Projetos 2.0 F2.1.
 *
 * Página enxuta de kanban pra workflows novos (Compras / Financeiro) sobre o
 * `<KanbanBoard>` universal. NÃO reusa a `ComercialPage` (565 linhas, acoplada
 * a 'comercial' + catálogos/filtros) — pra não arriscar o Comercial em produção.
 *
 * Card store = `deals` filtrados por `workflowSlug` (mesmo motor do Comercial).
 * Colunas vêm do seed (Migration 168) via `useKanbanColumns` dentro do
 * KanbanBoard. Os boards começam VAZIOS — os cards chegam pelas solicitações
 * dos projetos (F2.2).
 *
 * Gating: a tela só é montada quando a flag do workflow está ON (decisão no
 * `MenuPage`). Aqui não há gating — é responsabilidade do caller.
 */
import { useDraggable } from '@dnd-kit/core';

import { KanbanBoard, type KanbanCardProps } from '@/components/kanban/KanbanBoard';
import { PageHeader } from '@/components/ui';
import { type DefaultStage } from '@/lib/use-kanban-columns';
import { useDealsQuery, useDealActions } from '@/lib/comercial-store';
import { type Deal } from '@/lib/comercial-types';
import { useClientNome } from '@/lib/clients-store';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/cn';

// ─── Card enxuto ─────────────────────────────────────────────────────────

function SimpleDealCard({ item: deal }: KanbanCardProps<Deal>) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: deal.id,
  });
  const clienteNome = useClientNome(deal.clienteId);
  const clienteDisplay =
    clienteNome || (deal.clienteId.startsWith('client-') ? '…' : deal.clienteId);
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 10 }
    : undefined;
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      data-testid={`kanban-card-${deal.id}`}
      className={cn(
        'bg-paper-surface dark:bg-paper-dark-surface rounded-lg border border-line dark:border-line-dark p-3 cursor-grab active:cursor-grabbing transition-shadow',
        isDragging ? 'opacity-50 shadow-lg' : 'hover:shadow-sm',
      )}
    >
      <div className="text-xs font-mono text-ink-3 dark:text-ink-dark-3">{deal.humanLabel}</div>
      <div className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 truncate">
        {clienteDisplay}
      </div>
      {deal.valor > 0 && (
        <div className="text-xs text-ink-2 dark:text-ink-dark-2 mt-1">
          {formatCurrency(deal.valor)}
        </div>
      )}
    </div>
  );
}

// ─── Página ──────────────────────────────────────────────────────────────

export function WorkflowKanbanPage({
  workflowSlug,
  titulo,
  descricao,
  defaultStages,
}: {
  workflowSlug: string;
  titulo: string;
  descricao?: string;
  defaultStages: DefaultStage[];
}) {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;
  const { data: deals = [], isLoading } = useDealsQuery(workflowSlug);
  const { changeStage } = useDealActions(workflowSlug);

  return (
    // LAYOUT-CANONICO: o `p-4 md:p-6` próprio saiu — quem dá a margem é o
    // PageContainer do MenuPage (que monta esta tela). Sem isso o padding
    // ficava DOBRADO. O `fillHeight` é o par do `alturaCheia` de lá.
    <>
      {/* FASE-C: PageHeader canônico (h1 + subtítulo). */}
      <PageHeader title={titulo} subtitle={descricao} className="mb-4" />
      {isLoading ? (
        <div className="text-sm text-ink-3 dark:text-ink-dark-3 italic">Carregando…</div>
      ) : (
        <KanbanBoard<Deal>
          workflowSlug={workflowSlug}
          defaultStages={defaultStages}
          items={deals}
          getItemStage={(d) => d.fase}
          cardComponent={SimpleDealCard}
          onStageChange={async ({ item, toSlug, fromLabel, toLabel }) => {
            await changeStage(item.id, toSlug, '', '', { from: fromLabel, to: toLabel });
          }}
          isAdmin={isAdmin}
          showColumnTotals
          getItemValue={(d) => d.valor}
          emptyState={{
            title: 'Sem cards ainda',
            description: 'Os cards chegam pelas solicitações dos projetos.',
          }}
          fillHeight
        />
      )}
    </>
  );
}
